import type { FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react';
import type { QueryReturnValue } from '@reduxjs/toolkit/query';
import { refreshToken } from 'src/services/FetchRequest';
import { PATH_AUTH_SPA } from 'src/constants/spa-routes';
import { ERROR_MESSAGE_FRIENDLY } from 'src/shared/constants';

// Server and network failures carry a user-facing message in `error`.
export type AppBaseQueryError = FetchBaseQueryError & { error?: string };

export type AppBaseQueryResult = QueryReturnValue<unknown, AppBaseQueryError, FetchBaseQueryMeta>;

export interface StoredUser {
  access?: string;
  access_token?: string;
  refresh?: string;
  refresh_token?: string;
  [key: string]: unknown;
}

export const getStoredUser = (): StoredUser => {
  const raw = localStorage.getItem('user');
  const user = raw && raw !== 'undefined' && JSON.parse(raw);

  return user || {};
};

/**
 * Exchanges the refresh token for a new access token after a 401. Returns `true` when the new
 * token was stored, so the caller's retry repeats the request. Otherwise the stored user is
 * removed, the browser is sent to sign in, and `false` is returned.
 *
 * `refreshToken` resolves to an `Error` (never rejects) when the refresh is refused, so any
 * response without an `access_token` means the session is over.
 */
export const renewSession = async (refresh: string | undefined): Promise<boolean> => {
  const token = await refreshToken(refresh);
  const accessToken = token instanceof Error || token === true ? undefined : token?.access_token;

  if (accessToken) {
    localStorage.setItem('user', JSON.stringify({ ...getStoredUser(), access_token: accessToken }));
    return true;
  }

  localStorage.removeItem('user');
  globalThis.location.assign(PATH_AUTH_SPA.ROOT);
  return false;
};

export const withFriendlyServerError = (result: AppBaseQueryResult): AppBaseQueryResult => {
  const status = result.error?.status;

  if (status === 'FETCH_ERROR' || status === 500) {
    result.error.error = ERROR_MESSAGE_FRIENDLY;
  }

  return result;
};
